import { ChakraV3 } from '@piccolohealth/ui';
import type { Editor } from '@tiptap/react';
import type { NodeRangeSelection } from '@tiptap-pro/extension-node-range';
import React from 'react';
import { FaGripVertical, FaRemoveFormat, FaTrash } from 'react-icons/fa';
import { AddNodeMenu } from './AddNodeMenu';
import { useDragHandle } from './DragHandle';

type Props = {
  editor: Editor;
};

export const DragAndPlusButton = (props: Props) => {
  const { editor } = props;

  const dragHandleDisclosure = ChakraV3.useDisclosure();
  const addNodeDisclosure = ChakraV3.useDisclosure();
  const [selection, setSelection] = React.useState<NodeRangeSelection | null>(null);
  const [isLocked, setIsLocked] = React.useState(false);

  React.useEffect(() => {
    dragHandleDisclosure.open || addNodeDisclosure.open ? setIsLocked(true) : setIsLocked(false);
  }, [dragHandleDisclosure.open, addNodeDisclosure.open]);

  const onOpenAddNodeMenu = React.useCallback(
    (isOpen: boolean) => {
      if (isOpen) {
        addNodeDisclosure.onOpen();
      } else {
        addNodeDisclosure.onClose();
      }
    },
    [addNodeDisclosure],
  );

  const onClickRemoveFormatting = React.useCallback(() => {
    if (!selection) {
      return;
    }

    editor.chain().focus().setNodeSelection(selection.from).unsetAllMarks().setParagraph().run();
  }, [editor, selection]);

  const onClickDelete = React.useCallback(() => {
    if (!selection) {
      return;
    }

    editor.chain().focus().deleteRange({ from: selection.from, to: selection.to }).run();
  }, [editor, selection]);

  const childrenRef = React.useRef<HTMLDivElement>(null);
  const dragHandleRef = React.useRef<HTMLButtonElement>(null);

  const dragHandle = useDragHandle({
    editor,
    onSelection: setSelection,
    isLocked,
    childrenRef,
    dragHandleRef,
  });

  const onOpenDragHandleMenu = React.useCallback(
    (e: React.MouseEvent) => {
      dragHandle.onDragClick(e);
      dragHandleDisclosure.onToggle();
    },
    [dragHandle, dragHandleDisclosure],
  );

  const getAnchorRect = React.useCallback(() => {
    return dragHandleRef.current?.getBoundingClientRect() ?? null;
  }, []);

  return (
    <ChakraV3.HStack gap={1} ref={childrenRef} lineHeight='normal'>
      <AddNodeMenu editor={editor} isOpen={addNodeDisclosure.open} setOpen={onOpenAddNodeMenu} />
      <ChakraV3.IconButton
        ref={dragHandleRef}
        draggable
        onDragStart={dragHandle.onDragStart}
        onClick={onOpenDragHandleMenu}
        tabIndex={-1}
        variant='ghost'
        aria-label='Drag handle'
        fontSize='10px'
        size='2xs'
        color='gray.500'
        cursor='grab'
      >
        <FaGripVertical />
      </ChakraV3.IconButton>
      <ChakraV3.Menu.Root
        open={dragHandleDisclosure.open}
        lazyMount
        positioning={{
          placement: 'bottom-start',
          getAnchorRect,
        }}
        onInteractOutside={dragHandleDisclosure.onClose}
      >
        <ChakraV3.Menu.Positioner>
          <ChakraV3.Menu.Content>
            <ChakraV3.MenuItem onClick={onClickRemoveFormatting} value='remove-formatting'>
              <FaRemoveFormat />
              Clear formatting
            </ChakraV3.MenuItem>
            <ChakraV3.Menu.Separator />
            <ChakraV3.MenuItem onClick={onClickDelete} color='danger' value='delete'>
              <FaTrash />
              Delete
            </ChakraV3.MenuItem>
          </ChakraV3.Menu.Content>
        </ChakraV3.Menu.Positioner>
      </ChakraV3.Menu.Root>
    </ChakraV3.HStack>
  );
};
